import type { TokenCredential } from "@azure/identity";
import { authedJsonPagedValues } from "./http.js";
import { LocalCliError } from "./errors.js";

const ARM = "https://management.azure.com";
const ARM_SCOPE = "https://management.azure.com/.default";
const STORAGE_API = "2023-05-01";
const LA_API = "2023-09-01";

export interface StorageEntry {
  name?: string;
  properties?: { primaryEndpoints?: { table?: string }; tags?: Record<string, string> };
  tags?: Record<string, string>;
}
interface WorkspaceEntry { name?: string; properties?: { customerId?: string } }

export interface LedgerResources {
  ledgerTableEndpoint: string; // https://<account>.table.core.windows.net
  workspaceId: string; // LA customerId (the data-plane query id)
}

const tagOf = (a: StorageEntry, key: string) => a.tags?.[key] ?? a.properties?.tags?.[key];

/**
 * Pick the ledger storage account from a resource group's table-capable
 * accounts. An explicit name always wins. Otherwise: the deployed role tag
 * (`m8t: storage`), then any m8t-managed account, then a legacy hand-applied
 * `m8t:role: ledger`, then the lone candidate. Returns undefined when nothing
 * is provably the ledger — the caller reports rather than guessing, because
 * picking the wrong account silently reads an empty ledger.
 */
export function selectLedgerAccount(
  accounts: StorageEntry[],
  storageAccount?: string,
): StorageEntry | undefined {
  if (storageAccount) return accounts.find((a) => a.name === storageAccount);
  const managed = accounts.filter((a) => tagOf(a, "managedBy") === "m8t");
  return (
    managed.find((a) => tagOf(a, "m8t") === "storage") ??
    managed.at(0) ??
    accounts.find((a) => tagOf(a, "m8t:role") === "ledger") ??
    (accounts.length === 1 ? accounts[0] : undefined)
  );
}

/**
 * Discover the AgentLedger storage account's table endpoint + the Log
 * Analytics workspace customerId in the given resource group.
 *
 * Selection follows the tags the deployment actually applies (deploy/main.bicep:
 * every resource carries `managedBy: m8t` plus a role tag `m8t: <role>`, and the
 * ledger storage account is `m8t: storage`) — the same signal the MCP server's
 * storage discovery uses. An explicit `storageAccount` name always wins, for
 * installs whose resources predate the tags or were provisioned by hand.
 */
export async function discoverLedgerResources(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  /** Explicit account name; skips tag-based selection entirely. */
  storageAccount?: string;
}): Promise<LedgerResources> {
  const { credential, subscriptionId, resourceGroup, storageAccount } = opts;

  // Paginated: ARM may return empty early pages carrying only a continuation
  // token, and a single-page read would report "no ledger storage account" for
  // a resource group that has one. See authedJsonPagedValues.
  const storage = await authedJsonPagedValues<StorageEntry>({
    credential,
    scope: ARM_SCOPE,
    url: `${ARM}/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.Storage/storageAccounts?api-version=${STORAGE_API}`,
  });
  const accounts = storage.filter((a) => a.properties?.primaryEndpoints?.table);
  const ledgerAcct = selectLedgerAccount(accounts, storageAccount);
  const ledgerTableEndpoint = ledgerAcct?.properties?.primaryEndpoints?.table?.replace(/\/$/, "");
  if (!ledgerTableEndpoint) {
    const found = accounts.map((a) => a.name).join(", ") || "(none)";
    throw new LocalCliError({
      code: "DREAM_LEDGER_STORAGE_NOT_FOUND",
      message: storageAccount
        ? `Storage account '${storageAccount}' was not found in resource group ${resourceGroup}, or has no table endpoint.`
        : `No AgentLedger storage account found in resource group ${resourceGroup}.`,
      hint: `Found: ${found}. Pass --storage-account <name> to select one explicitly.`,
    });
  }

  const ws = await authedJsonPagedValues<WorkspaceEntry>({
    credential,
    scope: ARM_SCOPE,
    url: `${ARM}/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.OperationalInsights/workspaces?api-version=${LA_API}`,
  });
  const workspaceId = (ws.length === 1 ? ws[0] : ws.find((w) => w.properties?.customerId))?.properties?.customerId;
  if (!workspaceId) {
    throw new LocalCliError({
      code: "DREAM_LA_WORKSPACE_NOT_FOUND",
      message: `No Log Analytics workspace found in resource group ${resourceGroup}.`,
      hint: `Found: ${ws.map((w) => w.name).join(", ") || "(none)"}.`,
    });
  }

  return { ledgerTableEndpoint, workspaceId };
}
